import { useEffect, useState } from "react";
import "../css/quote.css";
import { getInventory, getDesignItem } from "../services/inventoryService";
import { initializePayment } from "../services/paymentService";

const MAX_FILE_SIZE = 20 * 1024 * 1024;

const initialForm = {
  fullName: "",
  phoneNumber: "",
  email: "",
};

function createJob(inventory = []) {
  const firstItem = inventory[0];

  return {
    id: Date.now() + Math.random(),
    inventoryId: firstItem?.inventoryId || "",
    width: "",
    height: "",
    quantity: 1,
    artwork: null,
    designRequired: false,
    designBrief: "",
    designContent: null,
  };
}

function formatPriceStatic(value) {
  return `₦${Number(value || 0).toLocaleString("en-NG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatUploadedSize(bytes) {
  if (!bytes) return "";

  const mb = bytes / (1024 * 1024);

  return `${mb.toFixed(2)} MB`;
}

function getSizeLabel(item) {
  if (!item?.size) return "";

  return item.size;
}

function calculateTotalCharged(deposit) {
  const FEE_RATE = 0.015;
  const FLAT_FEE = deposit < 2500 ? 0 : 100;
  const applicable = deposit * FEE_RATE + FLAT_FEE;

  if (applicable >= 2000) {
    return deposit + 2000;
  }

  return Number(
    (((deposit + FLAT_FEE) / (1 - FEE_RATE)) + 0.01).toFixed(2)
  );
}

function calculateFee(deposit) {
  return calculateTotalCharged(deposit) - deposit;
}

function normalizeCategory(category) {
  return String(category || "")
    .trim()
    .replace(/\s+/g, "")
    .toUpperCase();
}

function isLargeFormat(item) {
  return normalizeCategory(item?.category) === "LARGEFORMAT";
}

function isDesignProduct(item, designItem) {
  if (!item || !designItem) return false;

  return (
    Number(item.inventoryId) === Number(designItem.inventoryId)
  );
}

function ArtworkUploader({ job, updateJob, uploadError }) {
  const handleArtworkChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      updateJob(job.id, {
        artwork: null,
      });
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      updateJob(job.id, {
        artwork: null,
      });

      return;
    }

    updateJob(job.id, {
      artwork: {
        file,
        name: file.name,
        size: file.size,
      },
    });
  };

  return (
    <div className="artwork-section">
      <div className="section-label-row">
        <label className="section-label">
          Your artwork
        </label>

        <span className="section-hint">
          Max 20MB
        </span>
      </div>

      <label className="upload-box">
        <input
          type="file"
          onChange={handleArtworkChange}
        />

        <span className="upload-title">
          {job.artwork
            ? "Change artwork"
            : "Upload artwork"}
        </span>

        <span className="upload-subtitle">
          Upload your artwork in any file format
        </span>
      </label>

      {job.artwork && (
        <div className="uploaded-file">
          <div>
            <strong>{job.artwork.name}</strong>

            <span>
              {formatUploadedSize(job.artwork.size)}
            </span>
          </div>

          <button
            type="button"
            onClick={() =>
              updateJob(job.id, {
                artwork: null,
              })
            }
          >
            Remove
          </button>
        </div>
      )}

      {uploadError && (
        <p className="upload-error">
          {uploadError}
        </p>
      )}
    </div>
  );
}

function DesignUpload({ job, updateJob, handleDesignUpload }) {
  return (
    <div className="artwork-section">
      <div className="section-label-row">
        <label className="section-label">
          Reference files
        </label>

        <span className="section-hint">
          ZIP only · Max 20MB
        </span>
      </div>

      <label className="upload-box">
        <input
          type="file"
          accept=".zip,application/zip,application/x-zip-compressed"
          onChange={(event) =>
            handleDesignUpload(
              job.id,
              event.target.files?.[0]
            )
          }
        />

        <span className="upload-title">
          {job.designContent
            ? "Change ZIP"
            : "Upload reference ZIP"}
        </span>

        <span className="upload-subtitle">
          Upload any reference content for the designer
        </span>
      </label>

      {job.designContent && (
        <div className="uploaded-file">
          <div>
            <strong>
              {job.designContent.name}
            </strong>

            <span>
              {formatUploadedSize(
                job.designContent.size
              )}
            </span>
          </div>

          <button
            type="button"
            onClick={() =>
              updateJob(job.id, {
                designContent: null,
              })
            }
          >
            Remove
          </button>
        </div>
      )}
    </div>
  );
}

function DesignPanel({
  job,
  updateJob,
  handleDesignUpload,
}) {
  return (
    <div className="design-panel">
      <div className="field-group">
        <label>
          Design brief
        </label>

        <textarea
          value={job.designBrief}
          onChange={(event) =>
            updateJob(job.id, {
              designBrief: event.target.value,
            })
          }
          placeholder="Tell us what you'd like us to design..."
          rows="4"
        />
      </div>

      <DesignUpload
        job={job}
        updateJob={updateJob}
        handleDesignUpload={handleDesignUpload}
      />
    </div>
  );
}

function GenericRenderer({ job, item, updateJob }) {
  return (
    <div className="configuration-section">
      <div className="product-meta">
        <div>
          <span className="product-price">
            {formatPriceStatic(item?.price)}
          </span>

          {getSizeLabel(item) && (
            <span className="product-size">
              {getSizeLabel(item)}
            </span>
          )}
        </div>
      </div>

      <div className="quantity-section">
        <label>Quantity</label>

        <div className="quantity-control">
          <button
            type="button"
            onClick={() =>
              updateJob(job.id, {
                quantity: Math.max(
                  1,
                  Number(job.quantity || 1) - 1
                ),
              })
            }
          >
            −
          </button>

          <input
            type="number"
            min="1"
            value={job.quantity}
            onChange={(event) =>
              updateJob(job.id, {
                quantity: Math.max(
                  1,
                  Number(event.target.value) || 1
                ),
              })
            }
          />

          <button
            type="button"
            onClick={() =>
              updateJob(job.id, {
                quantity:
                  Number(job.quantity || 1) + 1,
              })
            }
          >
            +
          </button>
        </div>
      </div>
    </div>
  );
}

function LargeFormatRenderer({ job, item, updateJob }) {
  const area =
    Number(job.width || 0) *
    Number(job.height || 0);

  return (
    <div className="configuration-section">
      <div className="product-meta">
        <div>
          <span className="product-price">
            {formatPriceStatic(item?.price)}
          </span>

          <span className="product-size">
            per ft²
          </span>
        </div>
      </div>

      <div className="dimension-grid">
        <div className="field-group">
          <label>Width (ft)</label>

          <input
            type="number"
            min="0"
            step="0.01"
            value={job.width}
            onChange={(event) =>
              updateJob(job.id, {
                width: event.target.value,
              })
            }
            placeholder="Width"
          />
        </div>

        <div className="field-group">
          <label>Height (ft)</label>

          <input
            type="number"
            min="0"
            step="0.01"
            value={job.height}
            onChange={(event) =>
              updateJob(job.id, {
                height: event.target.value,
              })
            }
            placeholder="Height"
          />
        </div>
      </div>

      {area > 0 && (
        <div className="area-info">
          Area: {area.toFixed(2)} ft²
        </div>
      )}

      <div className="quantity-section">
        <label>Quantity</label>

        <div className="quantity-control">
          <button
            type="button"
            onClick={() =>
              updateJob(job.id, {
                quantity: Math.max(
                  1,
                  Number(job.quantity || 1) - 1
                ),
              })
            }
          >
            −
          </button>

          <input
            type="number"
            min="1"
            value={job.quantity}
            onChange={(event) =>
              updateJob(job.id, {
                quantity: Math.max(
                  1,
                  Number(event.target.value) || 1
                ),
              })
            }
          />

          <button
            type="button"
            onClick={() =>
              updateJob(job.id, {
                quantity:
                  Number(job.quantity || 1) + 1,
              })
            }
          >
            +
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Quote() {
  const [form, setForm] = useState(initialForm);

  const [inventory, setInventory] = useState([]);
  const [designItem, setDesignItem] = useState(null);

  const [jobs, setJobs] = useState([
    createJob(),
  ]);

  const [paymentPercent, setPaymentPercent] =
    useState(50);

  const [uploadError, setUploadError] =
    useState("");

  const [apiError, setApiError] =
    useState("");

  const [loadingInventory, setLoadingInventory] =
    useState(true);

  const [backendActive, setBackendActive] =
    useState(true);

  const [submitting, setSubmitting] =
    useState(false);

  useEffect(() => {
    let mounted = true;

    async function loadInventory() {
      setLoadingInventory(true);

      try {
        const [
          inventoryResponse,
          designResponse,
        ] = await Promise.all([
          getInventory(),
          getDesignItem(),
        ]);

        if (!mounted) return;

        const products =
          Array.isArray(inventoryResponse.data)
            ? inventoryResponse.data
            : [];

        setInventory(products);

        const design =
          designResponse?.data &&
          Object.keys(designResponse.data).length > 0
            ? designResponse.data
            : null;

        setDesignItem(design);

        setJobs((currentJobs) => {
          if (!currentJobs.length) {
            return [createJob(products)];
          }

          return currentJobs.map((job) => {
            if (job.inventoryId) {
              return job;
            }

            return {
              ...job,
              inventoryId:
                products[0]?.inventoryId || "",
            };
          });
        });

        setBackendActive(true);
      } catch (error) {
        console.error(error);

        if (mounted) {
          setBackendActive(false);

          setApiError(
            "We couldn't load the available products right now."
          );
        }
      } finally {
        if (mounted) {
          setLoadingInventory(false);
        }
      }
    }

    loadInventory();

    return () => {
      mounted = false;
    };
  }, []);

  function getInventoryItem(job) {
    return inventory.find(
      (item) =>
        Number(item.inventoryId) ===
        Number(job.inventoryId)
    );
  }

  function updateForm(field, value) {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  }

  function updateJob(jobId, changes) {
    setJobs((currentJobs) =>
      currentJobs.map((job) =>
        job.id === jobId
          ? {
              ...job,
              ...changes,
            }
          : job
      )
    );
  }

  function changeInventory(jobId, inventoryId) {
    const item = inventory.find(
      (entry) =>
        Number(entry.inventoryId) ===
        Number(inventoryId)
    );

    const designProduct = isDesignProduct(
      item,
      designItem
    );

    updateJob(jobId, {
      inventoryId,
      width: "",
      height: "",
      designRequired: designProduct,
      artwork: null,
      designBrief: "",
      designContent: null,
    });
  }

  function handleDesignUpload(jobId, file) {
    if (!file) {
      updateJob(jobId, {
        designContent: null,
      });

      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setUploadError(
        "Design ZIP files must be 20MB or smaller."
      );

      return;
    }

    setUploadError("");

    updateJob(jobId, {
      designContent: {
        file,
        name: file.name,
        size: file.size,
      },
    });
  }

  function calculateJobTotal(job) {
    const item = getInventoryItem(job);

    if (!item) return 0;

    const quantity =
      Number(job.quantity) || 0;

    const price =
      Number(item.price) || 0;

    let total;

    if (isLargeFormat(item)) {
      const width =
        Number(job.width) || 0;

      const height =
        Number(job.height) || 0;

      total =
        price *
        width *
        height *
        quantity;
    } else {
      total = price * quantity;
    }

    if (
      job.designRequired &&
      designItem &&
      !isDesignProduct(item, designItem)
    ) {
      total +=
        Number(designItem.price) || 0;
    }

    return total;
  }

  const estimatedTotal = jobs.reduce(
    (total, job) =>
      total + calculateJobTotal(job),
    0
  );

  const depositAmount = Math.round(
    estimatedTotal *
      (paymentPercent / 100)
  );

  const processingFee =
    calculateFee(depositAmount);

  const totalCharged =
    calculateTotalCharged(
      depositAmount
    );

  function addJob() {
    setJobs((currentJobs) => [
      ...currentJobs,
      createJob(inventory),
    ]);
  }

  function removeJob(jobId) {
    setJobs((currentJobs) => {
      if (currentJobs.length === 1) {
        return currentJobs;
      }

      return currentJobs.filter(
        (job) => job.id !== jobId
      );
    });
  }

  function validateForm() {
    const errors = [];

    if (!form.fullName.trim()) {
      errors.push(
        "Your name is required."
      );
    }

    if (!form.phoneNumber.trim()) {
      errors.push(
        "Your phone number is required."
      );
    }

    if (!form.email.trim()) {
      errors.push(
        "Your email is required."
      );
    } else {
      const emailRegex =
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (
        !emailRegex.test(
          form.email.trim()
        )
      ) {
        errors.push(
          "Please enter a valid email address."
        );
      }
    }

    if (!jobs.length) {
      errors.push(
        "Please add at least one item."
      );
    }

    jobs.forEach((job, index) => {
      const item =
        getInventoryItem(job);

      if (!item) {
        errors.push(
          `Item ${index + 1}: please select a valid product.`
        );

        return;
      }

      const quantity =
        Number(job.quantity);

      if (
        !quantity ||
        quantity < 1
      ) {
        errors.push(
          `Item ${index + 1}: quantity must be at least 1.`
        );
      }

      if (isLargeFormat(item)) {
        const width =
          Number(job.width);

        const height =
          Number(job.height);

        if (
          !width ||
          width <= 0
        ) {
          errors.push(
            `Item ${index + 1}: width is required.`
          );
        }

        if (
          !height ||
          height <= 0
        ) {
          errors.push(
            `Item ${index + 1}: height is required.`
          );
        }

        if (
          item.minArea != null &&
          width > 0 &&
          height > 0 &&
          width * height <
            Number(item.minArea)
        ) {
          errors.push(
            `Item ${index + 1}: minimum area is ${item.minArea} ft².`
          );
        }
      }

      if (job.designRequired) {
        if (!designItem) {
          errors.push(
            `Item ${index + 1}: design service is unavailable.`
          );
        }

        if (
          !job.designBrief?.trim()
        ) {
          errors.push(
            `Item ${index + 1}: design brief is required.`
          );
        }

        if (
          !job.designContent?.file
        ) {
          errors.push(
            `Item ${index + 1}: please upload your design ZIP.`
          );
        }
      } else {
        if (!job.artwork?.file) {
          errors.push(
            `Item ${index + 1}: please upload your artwork.`
          );
        }
      }

      if (
        job.artwork?.file &&
        job.designContent?.file
      ) {
        errors.push(
          `Item ${index + 1}: choose either artwork or IGC design, not both.`
        );
      }

      if (
        job.artwork?.file &&
        job.artwork.file.size >
          MAX_FILE_SIZE
      ) {
        errors.push(
          `Item ${index + 1}: artwork must be 20MB or smaller.`
        );
      }

      if (
        job.designContent?.file &&
        job.designContent.file.size >
          MAX_FILE_SIZE
      ) {
        errors.push(
          `Item ${index + 1}: design ZIP must be 20MB or smaller.`
        );
      }
    });

    if (estimatedTotal <= 0) {
      errors.push(
        "Please add a valid item before continuing."
      );
    }

    return errors;
  }

  function buildPaymentFormData() {
    const request = {
      fullName:
        form.fullName.trim(),

      phoneNumber:
        form.phoneNumber.trim(),

      email:
        form.email.trim(),

      items: [],
    };

    const formData =
      new FormData();

    let artworkIdx = 0;
    let designIdx = 0;

    jobs.forEach((job) => {
      const item =
        getInventoryItem(job);

      const largeFormat =
        isLargeFormat(item);

      const requestItem = {
        inventoryId:
          Number(item.inventoryId),

        quantity:
          Number(job.quantity),

        size: null,

        width: largeFormat
          ? Number(job.width)
          : null,

        height: largeFormat
          ? Number(job.height)
          : null,

        dimensionUnit:
          largeFormat
            ? "ft"
            : null,

        specifications:
          job.specifications?.trim() ||
          null,

        message:
          job.message?.trim() ||
          null,

        designRequired:
          Boolean(
            job.designRequired
          ),

        designBrief:
          job.designRequired
            ? job.designBrief?.trim() ||
              null
            : null,

        artworkFileIndex:
          null,

        designFileIndex:
          null,
      };

      if (job.designRequired) {
        requestItem.designFileIndex =
          designIdx;

        formData.append(
          "designFiles",
          job.designContent.file,
          job.designContent.name
        );

        designIdx++;
      } else {
        requestItem.artworkFileIndex =
          artworkIdx;

        formData.append(
          "artworkFiles",
          job.artwork.file,
          job.artwork.name
        );

        artworkIdx++;
      }

      request.items.push(
        requestItem
      );
    });

    formData.append(
      "request",
      new Blob(
        [JSON.stringify(request)],
        {
          type: "application/json",
        }
      )
    );

    return formData;
  }

  async function handleSubmit(event) {
    event.preventDefault();

    setApiError("");
    setUploadError("");

    const errors =
      validateForm();

    if (errors.length > 0) {
      setApiError(
        errors[0]
      );

      return;
    }

    if (!backendActive) {
      setApiError(
        "The ordering service is currently unavailable. Please try again later."
      );

      return;
    }

    if (depositAmount <= 0) {
      setApiError(
        "Please add a valid item before continuing."
      );

      return;
    }

    setSubmitting(true);

    try {
      const formData =
        buildPaymentFormData();

      const response =
        await initializePayment(
          formData,
          depositAmount
        );

      const data =
        response.data;

      if (
        !data?.authorizationUrl ||
        !data?.reference
      ) {
        throw new Error(
          "Invalid payment response."
        );
      }

      sessionStorage.setItem(
        "paymentReference",
        data.reference
      );

      window.location.href =
        data.authorizationUrl;
    } catch (error) {
      console.error(error);

      if (
        error?.response?.status ===
        400
      ) {
        setApiError(
          "Please check your input and try again."
        );
      } else {
        setApiError(
          "Something went wrong. Please try again."
        );
      }

      setSubmitting(false);
    }
  }

  function renderInventory(job) {
    const item =
      getInventoryItem(job);

    if (!item) {
      return null;
    }

    if (isLargeFormat(item)) {
      return (
        <LargeFormatRenderer
          job={job}
          item={item}
          updateJob={updateJob}
        />
      );
    }

    return (
      <GenericRenderer
        job={job}
        item={item}
        updateJob={updateJob}
      />
    );
  }

  return (
    <main className="quote-page">
      <section className="quote-intro">
        <h1 className="quote-title">
          Get a quote
        </h1>

        <p className="quote-lead">
          Tell us what you need and we'll
          prepare your order.
        </p>
      </section>

      <section className="quote-body">
        <div className="quote-workspace">
          <div className="quote-left">
            <form
              className="quote-card"
              onSubmit={handleSubmit}
            >
              <div className="card-step">
                <span className="step-number">
                  01
                </span>

                <div>
                  <h2>
                    Contact details
                  </h2>

                  <p>
                    Where should we send your
                    order information?
                  </p>
                </div>
              </div>

              <div className="customer-fields">
                <div className="field-group">
                  <label>
                    Full name
                  </label>

                  <input
                    type="text"
                    value={
                      form.fullName
                    }
                    onChange={(event) =>
                      updateForm(
                        "fullName",
                        event.target.value
                      )
                    }
                    placeholder="Your full name"
                  />
                </div>

                <div className="field-group">
                  <label>
                    Phone number
                  </label>

                  <input
                    type="tel"
                    value={
                      form.phoneNumber
                    }
                    onChange={(event) =>
                      updateForm(
                        "phoneNumber",
                        event.target.value
                      )
                    }
                    placeholder="+234..."
                  />
                </div>

                <div className="field-group">
                  <label>
                    Email address
                  </label>

                  <input
                    type="email"
                    value={
                      form.email
                    }
                    onChange={(event) =>
                      updateForm(
                        "email",
                        event.target.value
                      )
                    }
                    placeholder="you@example.com"
                  />
                </div>
              </div>

              <div className="card-step">
                <span className="step-number">
                  02
                </span>

                <div>
                  <h2>
                    Your items
                  </h2>

                  <p>
                    Select what you'd like us
                    to produce.
                  </p>
                </div>
              </div>

              <div className="job-list">
                {jobs.map(
                  (job, index) => {
                    const item =
                      getInventoryItem(
                        job
                      );

                    const designProduct =
                      isDesignProduct(
                        item,
                        designItem
                      );

                    return (
                      <div
                        className="job-builder"
                        key={job.id}
                      >
                        <div className="job-builder-header">
                          <div>
                            <span className="job-number">
                              Item{" "}
                              {index + 1}
                            </span>
                          </div>

                          {jobs.length >
                            1 && (
                            <button
                              type="button"
                              className="remove-job"
                              onClick={() =>
                                removeJob(
                                  job.id
                                )
                              }
                            >
                              Remove
                            </button>
                          )}
                        </div>

                        <div className="product-selector">
                          <label>
                            Product
                          </label>

                          <select
                            value={
                              job.inventoryId
                            }
                            onChange={(
                              event
                            ) =>
                              changeInventory(
                                job.id,
                                event.target
                                  .value
                              )
                            }
                            disabled={
                              loadingInventory
                            }
                          >
                            <option value="">
                              {loadingInventory
                                ? "Loading products..."
                                : "Select a product"}
                            </option>

                            {inventory.map(
                              (
                                product
                              ) => (
                                <option
                                  key={
                                    product.inventoryId
                                  }
                                  value={
                                    product.inventoryId
                                  }
                                >
                                  {
                                    product.item
                                  }
                                </option>
                              )
                            )}
                          </select>
                        </div>

                        {item &&
                          renderInventory(
                            job
                          )}

                        {item && (
                          <div className="design-service">
                            {designProduct ? (
                              <>
                                <div className="design-product-heading">
                                  <div>
                                    <span className="design-product-label">
                                      Design service
                                    </span>

                                    <span className="design-product-description">
                                      Tell us what
                                      you'd like
                                      us to design.
                                    </span>
                                  </div>

                                  <strong>
                                    {formatPriceStatic(
                                      item.price
                                    )}
                                  </strong>
                                </div>

                                <DesignPanel
                                  job={job}
                                  updateJob={
                                    updateJob
                                  }
                                  handleDesignUpload={
                                    handleDesignUpload
                                  }
                                />
                              </>
                            ) : (
                              <>
                                <label className="design-toggle">
                                  <span className="design-toggle-copy">
                                    <strong>
                                      I want IGC to
                                      design this
                                    </strong>

                                    <small>
                                      Let our team
                                      create the
                                      artwork for
                                      you
                                    </small>
                                  </span>

                                  <span className="switch">
                                    <input
                                      type="checkbox"
                                      checked={Boolean(
                                        job.designRequired
                                      )}
                                      disabled={
                                        !designItem
                                      }
                                      onChange={(
                                        event
                                      ) =>
                                        updateJob(
                                          job.id,
                                          {
                                            designRequired:
                                              event
                                                .target
                                                .checked,

                                            artwork:
                                              event
                                                .target
                                                .checked
                                                ? null
                                                : job.artwork,

                                            designBrief:
                                              event
                                                .target
                                                .checked
                                                ? job.designBrief
                                                : "",

                                            designContent:
                                              event
                                                .target
                                                .checked
                                                ? job.designContent
                                                : null,
                                          }
                                        )
                                      }
                                    />

                                    <span className="switch-track">
                                      <span className="switch-thumb" />
                                    </span>
                                  </span>

                                  {designItem && (
                                    <strong className="design-price">
                                      +
                                      {formatPriceStatic(
                                        designItem.price
                                      )}
                                    </strong>
                                  )}
                                </label>

                                {job.designRequired ? (
                                  <DesignPanel
                                    job={job}
                                    updateJob={
                                      updateJob
                                    }
                                    handleDesignUpload={
                                      handleDesignUpload
                                    }
                                  />
                                ) : (
                                  <ArtworkUploader
                                    job={job}
                                    updateJob={
                                      updateJob
                                    }
                                    uploadError={
                                      uploadError
                                    }
                                  />
                                )}
                              </>
                            )}
                          </div>
                        )}

                        {item && (
                          <div className="job-estimate">
                            <span>
                              Item estimate
                            </span>

                            <strong>
                              {formatPriceStatic(
                                calculateJobTotal(
                                  job
                                )
                              )}
                            </strong>
                          </div>
                        )}
                      </div>
                    );
                  }
                )}
              </div>

              <button
                type="button"
                className="add-job"
                onClick={addJob}
              >
                + Add another item
              </button>

              {apiError && (
                <div className="api-error">
                  {apiError}
                </div>
              )}
            </form>
          </div>

          <div className="quote-right">
            <div className="builder-heading">
              <span className="step-number">
                03
              </span>

              <div>
                <h2>
                  Payment
                </h2>

                <p>
                  Choose how much you'd like
                  to pay now.
                </p>
              </div>
            </div>

            <div className="summary-card">
              <div className="summary-list">
                <div>
                  <span>
                    Order total
                  </span>

                  <strong>
                    {formatPriceStatic(
                      estimatedTotal
                    )}
                  </strong>
                </div>
              </div>

              <div className="payment-section">
                <label>
                  Deposit
                </label>

                <div className="payment-options">
                  {[50, 75, 100].map(
                    (percent) => (
                      <button
                        type="button"
                        key={percent}
                        className={
                          paymentPercent ===
                          percent
                            ? "active"
                            : ""
                        }
                        onClick={() =>
                          setPaymentPercent(
                            percent
                          )
                        }
                      >
                        {percent}%
                      </button>
                    )
                  )}
                </div>
              </div>

              <div className="summary-list">
                <div>
                  <span>
                    Your deposit (
                    {paymentPercent}%)
                  </span>

                  <strong>
                    {formatPriceStatic(
                      depositAmount
                    )}
                  </strong>
                </div>

                <div>
                  <span>
                    Payment processing fee
                  </span>

                  <strong>
                    {formatPriceStatic(
                      processingFee
                    )}
                  </strong>
                </div>
              </div>

              <div className="summary-divider" />

              <div className="summary-total">
                <span>
                  You will be charged
                </span>

                <strong>
                  {formatPriceStatic(
                    totalCharged
                  )}
                </strong>
              </div>

              <button
                type="button"
                className="summary-submit"
                disabled={
                  submitting ||
                  loadingInventory ||
                  estimatedTotal <= 0
                }
                onClick={
                  handleSubmit
                }
              >
                {submitting
                  ? "Preparing payment..."
                  : "Continue to payment"}
              </button>

              <p className="payment-note">
                You will be redirected to
                Paystack to complete your payment
                securely.
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}